import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { PdfPreviewProvider } from "./components/PdfPreview";
import Layout from "./components/Layout";
import { SyncProvider } from "./context/SyncContext";
import { ROLES } from "./lib/format";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Activate from "./pages/Activate";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Employees from "./pages/Employees";
import Staff from "./pages/Staff";
import Salaries from "./pages/Salaries";
import SubContracts from "./pages/SubContracts";
import Contractors from "./pages/Contractors";
import InternshipForm from "./pages/InternshipForm";
import MySchedule from "./pages/MySchedule";
import Welcome from "./pages/Welcome";
import MyProjects from "./pages/MyProjects";
import Income from "./pages/Income";
import Payments from "./pages/Payments";
import Expenditures from "./pages/Expenditures";
import FinancialStatements from "./pages/FinancialStatements";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import AuditTrail from "./pages/AuditTrail";
import SmsLogs from "./pages/SmsLogs";
import Quotations from "./pages/Quotations";
import Estimates from "./pages/Estimates";
import Contracts from "./pages/Contracts";
import Receipts from "./pages/Receipts";
import SolarCalculator from "./pages/SolarCalculator";
import SearchResults from "./pages/SearchResults";
import Portal from "./pages/Portal";
import Settings from "./pages/Settings";
import Checks from "./pages/Checks";
import Approvals from "./pages/Approvals";
import Requests from "./pages/Requests";
import AdditionalWorks from "./pages/AdditionalWorks";
import MobileMoney from "./pages/MobileMoney";
import Announcements from "./pages/Announcements";
import WorkProgram from "./pages/WorkProgram";
import RoleAudit from "./pages/RoleAudit";
import OfficialLetters from "./pages/OfficialLetters";

function ProjectsRoute() {
  const { user } = useAuth();
  return user?.role === ROLES.EMPLOYEE ? <MyProjects /> : <Projects />;
}

const shell = (node, roles) => (
  <ProtectedRoute roles={roles}>
    <Layout>{node}</Layout>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <SyncProvider>
          <PdfPreviewProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/portal/:token" element={<Portal />} />

            <Route path="/dashboard" element={shell(<Dashboard />, [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO, ROLES.SS, ROLES.EMPLOYEE, ROLES.INTERN])} />
            <Route path="/projects" element={shell(<ProjectsRoute />, [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO, ROLES.EMPLOYEE])} />
            <Route path="/projects/:id" element={shell(<ProjectDetail />, [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO, ROLES.EMPLOYEE])} />
            <Route path="/employees" element={shell(<Employees />, [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO, ROLES.SS])} />
            <Route path="/staff" element={shell(<Staff />, [ROLES.CEO, ROLES.FM])} />
            <Route path="/salaries" element={shell(<Salaries />, [ROLES.CEO, ROLES.FM])} />
            <Route path="/sub-contracts" element={shell(<SubContracts />, [ROLES.CEO, ROLES.ACEO, ROLES.GM, ROLES.FM])} />
            <Route path="/contractors" element={shell(<Contractors />, [ROLES.CEO, ROLES.ACEO, ROLES.GM, ROLES.FM])} />
            <Route path="/students" element={shell(<InternshipForm />, [ROLES.CEO, ROLES.ACEO, ROLES.GM, ROLES.SS, ROLES.INTERN])} />
            <Route path="/welcome" element={shell(<Welcome />, [ROLES.VISITOR])} />
            <Route path="/income" element={shell(<Income />, [ROLES.CEO, ROLES.FM, ROLES.ACEO])} />
            <Route path="/payments" element={shell(<Payments />, [ROLES.CEO, ROLES.FM, ROLES.ACEO])} />
            <Route path="/checks" element={shell(<Checks />, [ROLES.CEO, ROLES.FM, ROLES.ACEO])} />
            <Route path="/expenditures" element={shell(<Expenditures />, [ROLES.CEO, ROLES.FM, ROLES.ACEO])} />
            <Route path="/financial-statements" element={shell(<FinancialStatements />, [ROLES.CEO, ROLES.FM, ROLES.ACEO])} />
            <Route path="/reports" element={shell(<Reports />, [ROLES.CEO, ROLES.GM, ROLES.FM, ROLES.ACEO])} />
            <Route path="/quotations" element={shell(<Quotations />, [ROLES.CEO, ROLES.ACEO, ROLES.GM])} />
            <Route path="/estimates" element={shell(<Estimates />, [ROLES.CEO, ROLES.ACEO, ROLES.GM])} />
            <Route path="/quotation" element={shell(<Estimates key="contract" type="contract" />, [ROLES.CEO, ROLES.ACEO, ROLES.GM])} />
            <Route path="/contract-estimates" element={<Navigate to="/quotation" replace />} />
            <Route path="/contracts" element={shell(<Contracts />, [ROLES.CEO, ROLES.ACEO, ROLES.GM])} />
            <Route path="/official-letters" element={shell(<OfficialLetters />, [ROLES.CEO])} />
            <Route path="/solar-calculator" element={shell(<SolarCalculator />, [ROLES.CEO, ROLES.GM, ROLES.ACEO, ROLES.FM])} />
            <Route path="/receipts" element={shell(<Receipts />, [ROLES.CEO, ROLES.FM])} />
            <Route path="/approvals" element={shell(<Approvals />, [ROLES.CEO, ROLES.ACEO])} />
            <Route path="/requests" element={shell(<Requests />, [ROLES.CEO, ROLES.FM, ROLES.ACEO, ROLES.GM])} />
            <Route path="/additional-works" element={shell(<AdditionalWorks />, [ROLES.CEO, ROLES.GM, ROLES.ACEO])} />
            <Route path="/mobile-money" element={shell(<MobileMoney />, [ROLES.FM])} />
            <Route path="/announcements" element={shell(<Announcements />, [ROLES.CEO, ROLES.ACEO, ROLES.GM, ROLES.FM])} />
            <Route path="/work-program" element={shell(<WorkProgram />, [ROLES.CEO])} />
            <Route path="/my-schedule" element={shell(<MySchedule />, [ROLES.EMPLOYEE, ROLES.INTERN])} />
            <Route path="/users" element={shell(<Users />, [ROLES.CEO])} />
            <Route path="/audit-trail" element={shell(<AuditTrail />, [ROLES.CEO])} />
            <Route path="/role-audit" element={shell(<RoleAudit />, [ROLES.CEO])} />
            <Route path="/sms-logs" element={shell(<SmsLogs />, [ROLES.CEO])} />
            <Route path="/settings" element={shell(<Settings />, [ROLES.CEO])} />
            <Route path="/search" element={shell(<SearchResults />)} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
          </PdfPreviewProvider>
          </SyncProvider>
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
